import { useState } from "react";
import { SAUNA_ENQUIRY_OPTIONS } from "@/data/sauna-enquiry-options";
import { SaunaQuoteForm } from "@/components/sauna-quote-form";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { trackEvent } from "@/hooks/use-analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ThermometerSnowflake, Settings, ArrowLeft, Home, Building2, Store, Maximize2, Snowflake, Fan, Wrench, ArrowUpCircle, PenTool } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import FileUploader, { type UploadedFile } from "@/components/file-uploader";

type Category = "ac" | "hvac" | "sauna" | null;

export default function Quote() {
  const [category, setCategory] = useState<Category>(() => new URLSearchParams(window.location.search).get("category") === "sauna" ? "sauna" : null);
  const [step, setStep] = useState(() => new URLSearchParams(window.location.search).get("category") === "sauna" ? 2 : 0);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [formData, setFormData] = useState({
    service: "",
    roomType: "",
    roomSize: "",
    customSize: "",
    sunlight: "",
    name: "",
    email: "",
    phone: "",
    postcode: "",
    hvacService: (() => { const value = new URLSearchParams(window.location.search).get("service") || ""; return SAUNA_ENQUIRY_OPTIONS.includes(value) ? value : ""; })(),
    message: ""
  });
  const { toast } = useToast();

  const updateForm = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const [submitting, setSubmitting] = useState(false);

  const submitQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message || "Site survey requested",
          formType: category === "ac" ? "AC Site Survey Request" : category === "sauna" ? "Sauna Quote / Survey Request" : "HVAC Site Survey Request",
          extra: {
            ...(category === "ac" && {
              Service: formData.service,
              "Room Type": formData.roomType,
              "Room Size": formData.roomSize || formData.customSize,
              Sunlight: formData.sunlight,
            }),
            ...(category === "hvac" && {
              "HVAC Service": formData.hvacService,
            }),
            ...(category === "sauna" && {
              "Sauna Service": formData.hvacService,
            }),
            Postcode: formData.postcode,
          },
          attachments: files.map(f => ({ name: f.name, mimeType: f.mimeType, data: f.data })),
        }),
      });
      if (!res.ok) throw new Error("Failed to send");
      toast({
        title: "Quote request received!",
        description: "We'll review your details and send a quote shortly.",
      });
      setStep(99);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us on 020 8087 0906.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (category === "ac") {
      if (step === 1) {
        setCategory(null);
        setStep(0);
      } else {
        setStep(s => s - 1);
      }
    } else if (category === "hvac" || category === "sauna") {
      if (step === 1) {
        setCategory(null);
        setStep(0);
      } else if (step === 2) {
        setStep(1);
      }
    }
  };

  const acStepsCount = 5;
  const hvacStepsCount = 2;
  const totalSteps = category === "ac" ? acStepsCount : hvacStepsCount;
  const progress = category ? (step / totalSteps) * 100 : 0;

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      <Header />

      <main className="flex-1 flex flex-col items-center justify-center py-12 px-4">
        <div className="w-full max-w-3xl">
          
          {step > 0 && step < 99 && (
            <div className="mb-8">
              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden mb-4">
                <div 
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm font-medium text-muted-foreground">
                <span>Step {step} of {totalSteps}</span>
                <button onClick={goBack} className="flex items-center hover:text-foreground">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </button>
              </div>
            </div>
          )}

          <div className="bg-card rounded-3xl shadow-xl border border-border overflow-hidden min-h-[400px] relative">
            <AnimatePresence mode="wait">
              
              {step === 0 && (
                <motion.div 
                  key="step0"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full flex flex-col"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-4">Get a Free Quote</h2>
                  <p className="text-center text-muted-foreground mb-10">What type of system are you looking for?</p>
                  <div className="grid md:grid-cols-2 gap-6 flex-1">
                    <button 
                      onClick={() => { setCategory("ac"); setStep(1); }}
                      className="border-2 border-border rounded-2xl p-8 flex flex-col items-center justify-center gap-4 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 group"
                    >
                      <Snowflake className="w-14 h-14 text-primary group-hover:text-white transition-colors" />
                      <span className="text-xl font-bold font-display">Air Conditioning</span>
                      <span className="text-sm text-muted-foreground group-hover:text-white/80 transition-colors">Cooling, heating & climate control</span>
                    </button>
                    <button 
                      onClick={() => { setCategory("hvac"); updateForm("hvacService", ""); setStep(1); }}
                      className="border-2 border-border rounded-2xl p-8 flex flex-col items-center justify-center gap-4 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 group"
                    >
                      <Fan className="w-14 h-14 text-primary group-hover:text-white transition-colors" />
                      <span className="text-xl font-bold font-display">HVAC System</span>
                      <span className="text-sm text-muted-foreground group-hover:text-white/80 transition-colors">Ventilation, heating & ductwork</span>
                    </button>
                    <button
                      onClick={() => { setCategory("sauna"); updateForm("hvacService", ""); setStep(1); }}
                      className="md:col-span-2 border-2 border-border rounded-2xl p-8 flex flex-col items-center justify-center gap-4 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 group"
                    >
                      <span className="text-xl font-bold font-display">Sauna</span>
                      <span className="text-sm text-muted-foreground group-hover:text-white/80">Advise & Design, Supply, Install, Maintenance & Repair</span>
                    </button>
                  </div>
                </motion.div>
              )}

              {category === "ac" && step === 1 && (
                <motion.div 
                  key="ac-step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full flex flex-col"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-10">Choose an Air Conditioning Type</h2>
                  <div className="grid md:grid-cols-2 gap-6 flex-1">
                    {[
                      { id: "Installation", icon: <ThermometerSnowflake className="w-12 h-12" />, desc: "New AC system design & install" },
                      { id: "Repair", icon: <Wrench className="w-12 h-12" />, desc: "Fix faults & restore cooling" },
                      { id: "Maintenance", icon: <Settings className="w-12 h-12" />, desc: "Scheduled servicing & inspections" },
                      { id: "Upgrade", icon: <ArrowUpCircle className="w-12 h-12" />, desc: "Modernise your existing system" }
                    ].map(opt => (
                      <button 
                        key={opt.id}
                        onClick={() => { updateForm("service", opt.id); setStep(2); }}
                        className="border-2 border-border rounded-2xl p-6 flex flex-col items-center justify-center gap-3 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 group"
                      >
                        <div className="text-primary group-hover:text-white transition-colors">{opt.icon}</div>
                        <span className="text-xl font-bold font-display">{opt.id}</span>
                        <span className="text-sm text-muted-foreground group-hover:text-white/80 transition-colors">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {category === "ac" && step === 2 && (
                <motion.div 
                  key="ac-step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-8">Which of these best describes your room?</h2>
                  
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-semibold text-muted-foreground flex items-center gap-2 mb-4"><Home className="w-5 h-5"/> Domestic</h3>
                      <div className="flex flex-wrap gap-3">
                        {["Bedroom", "Lounge", "Kitchen", "Conservatory", "Gym", "Loft Conversion"].map(r => (
                          <button key={r} onClick={() => { updateForm("roomType", r); setStep(3); }} className="px-5 py-3 rounded-full border border-border bg-background hover:border-primary hover:text-primary font-medium transition-colors">
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-muted-foreground flex items-center gap-2 mb-4"><Building2 className="w-5 h-5"/> Offices & Schools</h3>
                      <div className="flex flex-wrap gap-3">
                        {["Office", "Classroom", "Server Room"].map(r => (
                          <button key={r} onClick={() => { updateForm("roomType", r); setStep(3); }} className="px-5 py-3 rounded-full border border-border bg-background hover:border-primary hover:text-primary font-medium transition-colors">
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-muted-foreground flex items-center gap-2 mb-4"><Store className="w-5 h-5"/> Shops & Restaurants</h3>
                      <div className="flex flex-wrap gap-3">
                        {["Shop", "Restaurant", "Commercial Kitchen"].map(r => (
                          <button key={r} onClick={() => { updateForm("roomType", r); setStep(3); }} className="px-5 py-3 rounded-full border border-border bg-background hover:border-primary hover:text-primary font-medium transition-colors">
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {category === "ac" && step === 3 && (
                <motion.div 
                  key="ac-step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full flex flex-col"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-10">What is the size of your room?</h2>
                  
                  <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    {[
                      { id: "Small", desc: "Up to 15m²" },
                      { id: "Medium", desc: "15m² to 30m²" },
                      { id: "Large", desc: "30m² to 50m²" }
                    ].map(s => (
                      <button 
                        key={s.id}
                        onClick={() => { updateForm("roomSize", s.id); updateForm("customSize", ""); setStep(4); }}
                        className="border border-border rounded-xl p-6 flex flex-col items-center gap-2 hover:border-primary hover:bg-slate-50 transition-colors"
                      >
                        <Maximize2 className="w-8 h-8 text-primary opacity-50" />
                        <span className="font-bold text-lg">{s.id}</span>
                        <span className="text-sm text-muted-foreground">{s.desc}</span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-auto">
                    <Label className="mb-2 block">Or enter custom dimensions (m² or sq ft)</Label>
                    <div className="flex gap-4">
                      <Input 
                        placeholder="e.g. 45m² or 5m x 4m" 
                        value={formData.customSize}
                        onChange={(e) => updateForm("customSize", e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        disabled={!formData.customSize} 
                        onClick={() => { updateForm("roomSize", "Custom"); setStep(4); }}
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {category === "ac" && step === 4 && (
                <motion.div 
                  key="ac-step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full flex flex-col items-center justify-center text-center"
                >
                  <h2 className="text-3xl font-display font-bold mb-10">Do you receive direct sunlight in the afternoon?</h2>
                  <div className="flex gap-6 justify-center mb-8">
                    <button 
                      onClick={() => updateForm("sunlight", "Yes")}
                      className={`w-32 h-32 rounded-full border-2 text-xl font-bold transition-colors ${formData.sunlight === "Yes" ? "border-primary bg-primary text-white" : "border-border hover:border-primary"}`}
                    >
                      Yes
                    </button>
                    <button 
                      onClick={() => updateForm("sunlight", "No")}
                      className={`w-32 h-32 rounded-full border-2 text-xl font-bold transition-colors ${formData.sunlight === "No" ? "border-primary bg-primary text-white" : "border-border hover:border-primary"}`}
                    >
                      No
                    </button>
                  </div>
                  <Button 
                    size="lg" 
                    disabled={!formData.sunlight} 
                    onClick={() => setStep(5)}
                    className="w-full max-w-xs"
                  >
                    Confirm
                  </Button>
                </motion.div>
              )}

              {category === "ac" && step === 5 && (
                <motion.div 
                  key="ac-step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-8">Site Survey Request</h2>
                  
                  <form onSubmit={submitQuote} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input required value={formData.name} onChange={e => updateForm("name", e.target.value)} placeholder="Full name" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input required type="email" value={formData.email} onChange={e => updateForm("email", e.target.value)} placeholder="you@example.com" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input required type="tel" value={formData.phone} onChange={e => updateForm("phone", e.target.value)} placeholder="07xxx xxxxxx" />
                      </div>
                      <div className="space-y-2">
                        <Label>Postcode</Label>
                        <Input required value={formData.postcode} onChange={e => updateForm("postcode", e.target.value)} placeholder="SW1A 1AA" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Describe your service request</Label>
                      <Textarea 
                        value={formData.message} 
                        onChange={e => updateForm("message", e.target.value)} 
                        placeholder="Tell us about your requirements, building type, any existing systems, etc."
                        className="min-h-[100px]"
                      />
                    </div>
                    <FileUploader files={files} onChange={setFiles} label="Attach photos, PDFs or Excel (optional)" />
                    
                    <div className="pt-4">
                      <Button type="submit" size="lg" disabled={submitting} className="w-full text-lg h-14">{submitting ? "Sending..." : "Request Free Site Survey"}</Button>
                      <p className="text-sm text-muted-foreground text-center mt-4 leading-relaxed">
                        Complete the form below to request a free site survey and a member of our expert team will contact you shortly to arrange a visit. You can also call us on <a href="tel:02080870906" className="text-primary font-semibold hover:underline" onClick={() => trackEvent("call_click", "Quote Form Inline")}>020 8087 0906</a> during office hours.
                      </p>
                      <p className="text-sm text-muted-foreground text-center mt-2 font-medium">
                        Lines open 8:45am–5pm (Mon–Fri)
                      </p>
                    </div>
                  </form>
                </motion.div>
              )}

              {(category === "hvac" || category === "sauna") && step === 1 && (
                <motion.div 
                  key="hvac-step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full flex flex-col"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-4">What {category === "sauna" ? "Sauna" : "HVAC"} service do you need?</h2>
                  <p className="text-center text-muted-foreground mb-10">Select the service that best matches your requirements.</p>
                  <div className="grid md:grid-cols-2 gap-6 flex-1">
                    {(category === "sauna" ? SAUNA_ENQUIRY_OPTIONS.map(id => ({
                      id, icon: <PenTool className="w-12 h-12" />, desc: "Tell us about your sauna requirements",
                    })) : [
                      { id: "Installation", icon: <PenTool className="w-12 h-12" />, desc: "New HVAC system design & install" },
                      { id: "Repair", icon: <Wrench className="w-12 h-12" />, desc: "Fix faults & restore performance" },
                      { id: "Maintenance", icon: <Settings className="w-12 h-12" />, desc: "Scheduled servicing & inspections" },
                      { id: "Upgrade", icon: <ArrowUpCircle className="w-12 h-12" />, desc: "Modernise your existing system" }
                    ]).map(opt => (
                      <button 
                        key={opt.id}
                        onClick={() => { updateForm("hvacService", opt.id); setStep(2); }}
                        className="border-2 border-border rounded-2xl p-6 flex flex-col items-center justify-center gap-3 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 group"
                      >
                        <div className="text-primary group-hover:text-white transition-colors">{opt.icon}</div>
                        <span className="text-xl font-bold font-display">{opt.id}</span>
                        <span className="text-sm text-muted-foreground group-hover:text-white/80 transition-colors">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {category === "sauna" && step === 2 && (
                <div className="p-6 md:p-10"><SaunaQuoteForm service={formData.hvacService || "Sauna — Install"} /></div>
              )}

              {category === "hvac" && step === 2 && (
                <motion.div 
                  key="hvac-step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 md:p-12 h-full"
                >
                  <h2 className="text-3xl font-display font-bold text-center mb-2">Site Survey Request</h2>
                  <p className="text-center text-muted-foreground mb-8">{category === "hvac" ? "HVAC " : ""}{formData.hvacService}</p>
                  
                  <form onSubmit={submitQuote} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input required value={formData.name} onChange={e => updateForm("name", e.target.value)} placeholder="Full name" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input required type="email" value={formData.email} onChange={e => updateForm("email", e.target.value)} placeholder="you@example.com" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input required type="tel" value={formData.phone} onChange={e => updateForm("phone", e.target.value)} placeholder="07xxx xxxxxx" />
                      </div>
                      <div className="space-y-2">
                        <Label>Postcode</Label>
                        <Input required value={formData.postcode} onChange={e => updateForm("postcode", e.target.value)} placeholder="SW1A 1AA" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Describe your service request</Label>
                      <Textarea 
                        value={formData.message} 
                        onChange={e => updateForm("message", e.target.value)} 
                        placeholder="Tell us about your requirements, building type, any existing systems, etc."
                        className="min-h-[100px]"
                      />
                    </div>
                    <FileUploader files={files} onChange={setFiles} label="Attach photos, PDFs or Excel (optional)" />
                    
                    <div className="pt-4">
                      <Button type="submit" size="lg" disabled={submitting} className="w-full text-lg h-14">{submitting ? "Sending..." : "Request Free Site Survey"}</Button>
                      <p className="text-sm text-muted-foreground text-center mt-4 leading-relaxed">
                        Complete the form below to request a free site survey and a member of our expert team will contact you shortly to arrange a visit. You can also call us on <a href="tel:02080870906" className="text-primary font-semibold hover:underline" onClick={() => trackEvent("call_click", "Quote Form Inline")}>020 8087 0906</a> during office hours.
                      </p>
                      <p className="text-sm text-muted-foreground text-center mt-2 font-medium">
                        Lines open 8:45am–5pm (Mon–Fri)
                      </p>
                    </div>
                  </form>
                </motion.div>
              )}

              {step === 99 && (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-12 h-full flex flex-col items-center justify-center text-center"
                >
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h2 className="text-3xl font-display font-bold mb-4">Request Sent!</h2>
                  <p className="text-lg text-muted-foreground mb-8">
                    Thank you{formData.name ? `, ${formData.name}` : ""}. Our team will review your requirements and get back to you with a tailored quote within 24 hours.
                  </p>
                  <Link href="/">
                    <Button variant="outline">Return to Home</Button>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
